import { Link } from "react-router-dom";
import { Wrench, ChevronLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen w-full bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-blue-700 flex items-center justify-center text-white">
            <Wrench className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-xl text-slate-900">
              Bell Appliance Repair
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">
              Terms of Service
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6 text-slate-700 leading-relaxed">
        <Link to="/book" className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline">
          <ChevronLeft className="w-4 h-4" /> Back to booking
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">
          Terms of Service
        </h1>
        <p className="text-xs text-slate-500 uppercase tracking-wider">
          Effective: February 2026
        </p>

        <section className="space-y-3">
          <p>
            These Terms of Service (&quot;Terms&quot;) govern your booking and receipt of
            appliance repair services from <strong>Bell Appliance Repair LLC</strong>{" "}
            (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). By booking an appointment with
            us — online, by phone, or by email — you agree to these Terms.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">1. Service Area</h2>
          <p>
            We provide appliance repair and installation services in Maryland (MD), Virginia
            (VA), and the District of Columbia (DC). If your service address falls outside our
            service area, we may decline the booking or refer you to another provider.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">2. Booking &amp; Scheduling</h2>
          <p>
            Appointments are booked in 2-hour arrival windows (e.g., 9:00 AM – 11:00 AM). We do
            our best to arrive within the window; on rare occasions traffic, prior-job overruns,
            or emergencies may cause delays. We will notify you if there is a significant delay.
          </p>
          <p>
            <strong>Cancellations:</strong> Please provide at least <strong>24 hours notice</strong>{" "}
            if you need to cancel or reschedule. Cancellations with less than 4 hours notice may
            be subject to a trip fee.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">
            3. Diagnostic &amp; Repair Fees
          </h2>
          <p>
            A diagnostic (service call) fee applies to every appointment. If you approve a
            repair, the diagnostic fee is typically applied toward the total repair cost. Repair
            estimates are provided after diagnosis and require your approval before work begins.
            Parts and labor are quoted separately when needed.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">4. Warranty</h2>
          <p>
            Repairs are warranted for <strong>30 days on labor</strong> and manufacturer-specified
            periods on parts. Warranty covers the specific repair performed; it does not cover
            new or unrelated issues, damage caused by misuse, or normal wear.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">5. Payment</h2>
          <p>
            Payment is due upon completion of the service. We accept cash, checks, and major
            credit/debit cards. Unpaid balances may incur reasonable collection costs.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">
            6. SMS &amp; Communications Consent
          </h2>
          <p>
            SMS text messaging from Bell Appliance Repair is <strong>strictly opt-in</strong>{" "}
            and is <strong>not required</strong> to book service or use our website. To
            receive SMS updates you must actively check the SMS consent box on our online
            booking form at{" "}
            <Link to="/book" className="text-blue-700 hover:underline">
              /book
            </Link>{" "}
            or provide verbal/written consent to our staff, which we record on your customer
            profile. If you do not opt in we&apos;ll reach you by phone or email only.
          </p>
          <p>
            When you opt in you consent to receive transactional SMS messages related to
            your appointment — booking confirmations, appointment reminders, and technician
            arrival notifications. Message frequency: up to 4 SMS messages per appointment.
            Message and data rates may apply. Reply <strong>STOP</strong> to opt out at any
            time. Reply <strong>HELP</strong> for support. See our{" "}
            <Link to="/privacy" className="text-blue-700 hover:underline">
              Privacy Policy
            </Link>{" "}
            for details on how we handle your information.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">7. Access &amp; Safety</h2>
          <p>
            An adult (18+) must be present at the service address during the appointment. You
            agree to provide safe access to the appliance being serviced (clear pathway,
            functional utilities, adequate lighting) and to disclose any known hazards.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">
            8. Limitation of Liability
          </h2>
          <p>
            To the fullest extent permitted by law, Bell Appliance Repair LLC&apos;s liability
            for any claim arising out of or relating to our services is limited to the amount
            you paid us for the specific service in question. We are not liable for indirect,
            incidental, or consequential damages (such as spoiled food from a delayed
            refrigerator repair).
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">9. Changes to Terms</h2>
          <p>
            We may update these Terms from time to time. Continued use of our services after any
            update constitutes acceptance of the revised Terms. The &quot;Effective&quot; date
            at the top indicates the most recent update.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">10. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Maryland, without regard to its
            conflict-of-laws principles. Any dispute will be resolved in the state or federal
            courts located in Prince George&apos;s County, Maryland.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">11. Contact</h2>
          <p>
            <strong>Bell Appliance Repair LLC</strong><br />
            2216 Rosedell Place<br />
            Fort Washington, MD 20744<br />
            Email:{" "}
            <a href="mailto:bellappliancerepair@gmail.com" className="text-blue-700 hover:underline">
              bellappliancerepair@gmail.com
            </a>
          </p>
        </section>

        <footer className="pt-8 border-t border-slate-200 text-xs text-slate-400">
          © {new Date().getFullYear()} Bell Appliance Repair LLC. All rights reserved.{" "}
          <Link to="/privacy" className="text-blue-700 hover:underline">
            Privacy Policy
          </Link>
        </footer>
      </main>
    </div>
  );
}
